import { html, nothing, unsafeCSS, type CSSResultGroup, type PropertyValues } from 'lit';
import { live } from 'lit/directives/live.js';
import { customElement, property, state } from 'lit/decorators.js';
import { LibElement } from '../lib-element';
import css from './lib-textbox.css';

const icons = {
  close: new URL('../wago-icons-svg/close.svg', import.meta.url).href,
  view: new URL('../wago-icons-svg/view.svg', import.meta.url).href,
  'no-view': new URL('../wago-icons-svg/no-view.svg', import.meta.url).href,
};

/** A text input with commit modes, validation feedback and optional password actions. */
@customElement('lib-textbox')
export class LibTextBox extends LibElement {
  protected override get hostFocusSelector(): string { return 'input'; }

  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(css)];

  /** Includes the visible draft in native HTML forms. */
  static readonly formAssociated = true;
  private readonly internals = typeof this.attachInternals === 'function' ? this.attachInternals() : undefined;

  /** Native form field name. */
  @property() name = '';
  /** Identity of the input and its internal label target. */
  @property({ attribute: 'input-id' }) inputId = '';
  /** Autofill hint forwarded to the input. */
  @property() autocomplete = '';
  /** Focuses the enabled input after its first render. */
  @property({ type: Boolean }) autofocus = false;
  @property({ attribute: 'clear-label' }) clearLabel = 'Clear text';
  @property({ attribute: 'show-password-label' }) showPasswordLabel = 'Show password';
  @property({ attribute: 'hide-password-label' }) hidePasswordLabel = 'Hide password';
  @property() value = '';
  private revision = 0;
  @property() headline = '';
  @property() placeholder = '';
  @property({ type: Number }) maxlength = 0;
  @property({ type: Boolean, attribute: 'show-maxlength' }) showMaxlength = false;
  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean }) readonly = false;
  @property({ type: Boolean }) password = false;
  @property({ attribute: 'bind-mode' }) bindMode = 'oninput';
  @property({ attribute: 'clear-mode' }) clearMode = 'never';
  @property({ type: Boolean }) error = false;
  @property() message = '';
  @property() surface = 'white';
  @property({ attribute: 'aria-label' }) override ariaLabel: string | null = null;
  @state() private draft = '';
  @state() private showPassword = false;
  @state() private formDisabled = false;
  private composing = false;
  private pendingValues: string[] = [];
  private timer?: ReturnType<typeof setTimeout>;

  private get effectivelyDisabled(): boolean { return this.disabled || this.formDisabled; }

  static override get observedAttributes(): string[] { return [...super.observedAttributes, 'server-state']; }

  override attributeChangedCallback(name: string, previous: string | null, value: string | null): void {
    if (name === 'server-state') {
      if (value !== null) {
        const state = JSON.parse(value) as { value: string; revision: number };
        if (state.revision >= this.revision) this.value = state.value;
      }
      return;
    }
    if (name === 'value' && this.hasAttribute('server-state')) return;
    if (name === 'value') {
      const pending = this.pendingValues.indexOf(value ?? '');
      if (pending >= 0) {
        this.pendingValues.splice(0, pending + 1);
        return;
      }
      this.pendingValues = [];
    }
    super.attributeChangedCallback(name, previous, value);
  }

  protected override willUpdate(changed: PropertyValues<this>): void {
    if (changed.has('value')) {
      this.cancelPending();
      this.draft = this.value;
    }
    if (changed.has('disabled') || changed.has('readonly') || changed.has('bindMode'))
      this.cancelPending();
    this.syncFormValue();
  }

  protected override firstUpdated(): void {
    if (this.autofocus && !this.effectivelyDisabled)
      this.renderRoot.querySelector('input')?.focus();
  }

  /** Restores the current value attribute and notifies the bound model. */
  formResetCallback(): void {
    this.cancelPending();
    this.composing = false;
    this.pendingValues = [];
    const initial = this.getAttribute('value') ?? '';
    const changed = this.value !== initial || this.draft !== initial;
    this.revision++;
    this.draft = initial;
    this.value = initial;
    this.syncFormValue();
    if (changed) this.notifyChange();
  }

  /** Applies the browser's disabled-fieldset state without changing the disabled attribute. */
  formDisabledCallback(disabled: boolean): void {
    this.formDisabled = disabled;
    if (disabled) this.cancelPending();
    this.syncFormValue();
  }

  private syncFormValue(): void {
    if (typeof this.internals?.setFormValue === 'function')
      this.internals.setFormValue(this.effectivelyDisabled ? null : this.draft);
  }

  override disconnectedCallback(): void {
    this.cancelPending();
    this.composing = false;
    this.pendingValues = [];
    super.disconnectedCallback();
  }

  private cancelPending(): void {
    clearTimeout(this.timer);
    this.timer = undefined;
  }

  private commit(): void {
    this.cancelPending();
    if (this.effectivelyDisabled || this.readonly || this.composing || this.value === this.draft) return;
    this.value = this.draft;
    if (this.hasAttribute('value') && !this.hasAttribute('server-state')) this.pendingValues.push(this.value);
    this.syncFormValue();
    this.notifyChange();
  }

  private notifyChange(): void {
    this.dispatchEvent(new CustomEvent('lib-textbox-change', {
      detail: { value: this.value, revision: this.revision }, bubbles: true, composed: true,
    }));
    if (!this.hasAttribute('server-state'))
      this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  private input(event: Event): void {
    event.stopPropagation();
    if (this.effectivelyDisabled || this.readonly) return;
    this.revision++;
    this.draft = (event.target as HTMLInputElement).value;
    this.syncFormValue();
    this.cancelPending();
    if (this.composing) return;
    if (this.bindMode === 'oninput') this.commit();
    else if (this.bindMode === 'ondelayedinput')
      this.timer = setTimeout(() => this.commit(), 300);
  }

  private change(event: Event): void {
    event.stopPropagation();
    this.commit();
  }

  private clear(): void {
    if (this.effectivelyDisabled || this.readonly) return;
    this.revision++;
    this.draft = '';
    this.commit();
    this.renderRoot.querySelector('input')?.focus();
  }

  private key(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.isComposing || this.composing) return;
    event.preventDefault();
    if (event.repeat || this.effectivelyDisabled) return;
    this.commit();
    const form = this.internals?.form;
    if (!form) return;
    const root = form.getRootNode() as Document | ShadowRoot;
    for (const control of root.querySelectorAll('button, input, lib-button')) {
      if (control instanceof HTMLButtonElement || control instanceof HTMLInputElement) {
        if (control.form === form && (control.type === 'submit' || control.type === 'image') && !control.matches(':disabled')) {
          control.click();
          return;
        }
      } else if (Array.from(form.elements).includes(control) && control instanceof HTMLElement && control.localName === 'lib-button'
        && control.hasAttribute('submit') && !control.hasAttribute('disabled')
        && !control.hasAttribute('loading') && !control.hasAttribute('href') && !control.matches(':disabled')) {
        control.shadowRoot?.querySelector<HTMLButtonElement>('button')?.click();
        return;
      }
    }
  }

  override render() {
    const disabled = this.effectivelyDisabled;
    const inputId = this.inputId || 'input';
    const invalid = this.error || !!this.message;
    const clear = !disabled && !this.readonly
      && (this.clearMode === 'always' || (this.clearMode === 'auto' && !!this.draft));
    const classes = `lib-textbox lib-textbox-surface-${this.surface} ${disabled ? 'lib-textbox-disabled' : ''} ${this.readonly ? 'lib-textbox-readonly' : ''} ${invalid ? 'lib-textbox-error' : ''}`;
    const passwordAction = this.renderPasswordAction();
    return html`<div class=${classes}>
      ${this.headline ? html`<div class="lib-textbox-label">
        <label class="lib-textbox-label-text" for=${inputId}>${this.headline}</label>
        <slot name="help"></slot>
      </div>` : nothing}
      <div class="lib-textbox-field">
        <input id=${inputId} name=${this.name || nothing} autocomplete=${this.autocomplete || nothing}
          class="lib-textbox-input" .value=${live(this.draft)}
          type=${this.password && !this.showPassword ? 'password' : 'text'}
          placeholder=${this.placeholder} maxlength=${this.maxlength > 0 ? this.maxlength : nothing}
          ?disabled=${disabled} ?readonly=${this.readonly}
          aria-label=${this.ariaLabel || nothing} aria-invalid=${invalid ? 'true' : nothing}
          aria-describedby=${this.message ? 'message' : nothing}
          @input=${this.input} @change=${this.change} @blur=${this.commit}
          @keydown=${this.key}
          @compositionstart=${() => { this.composing = true; this.cancelPending(); }}
          @compositionend=${(event: CompositionEvent) => { this.composing = false; this.input(event); }} />
        ${this.showMaxlength && this.maxlength > 0 ? html`<span class="lib-textbox-counter">${this.draft.length}/${this.maxlength}</span>` : nothing}
        <slot name="icon"></slot>
        ${clear ? this.action(this.clearLabel, 'close', () => this.clear()) : nothing}
        ${passwordAction}
      </div>
      ${this.message ? html`<div id="message" class="lib-textbox-message">${this.message}</div>` : nothing}
    </div>`;
  }

  private renderPasswordAction() {
    if (!this.password || this.effectivelyDisabled) return nothing;
    const label = this.showPassword ? this.hidePasswordLabel : this.showPasswordLabel;
    const icon = this.showPassword ? 'no-view' : 'view';
    return this.action(label, icon, () => { this.showPassword = !this.showPassword; });
  }

  private action(label: string, icon: keyof typeof icons, click: () => void) {
    return html`<button type="button" class="lib-textbox-action" aria-label=${label} @click=${click}
      @keydown=${(event: KeyboardEvent) => event.stopPropagation()}>
      <span class="icon" aria-hidden="true" style=${`--icon: url("${icons[icon]}")`}></span>
    </button>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'lib-textbox': LibTextBox; }
}
